import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { CalendarDemoComponent } from '../../demo/calendar-demo/calendar-demo.component';
import { CalendarClickEvent } from '../../models/calendar-click-event';
import { CalendarEventHandler } from '../../models/calendar-event-handler';
import { CalendarType } from '../../models/calendar-type';
import { CalendarMonthComponent } from '../calendar-month/calendar-month.component';
import { CalendarWeekComponent } from '../calendar-week/calendar-week.component';
import { CalendarYearComponent } from '../calendar-year/calendar-year.component';
import { DayTemplateContext, ICalendarComponent } from '../../interface/calendar';
import { CalendarSettings } from '../../models/calendar-settings';

/**
 * A component groups together a week, month and year calendar.
 * 
 * It consists of:
 * - A header to switch between views, and dates.
 * - {@link CalendarWeekComponent}
 * - {@link CalendarMonthComponent}
 * - {@link CalendarYearComponent}
 * 
 * The demo can be found at {@link CalendarDemoComponent}.  
 * Live demo [here](https://localhost/shared/calendar).
 * 
 * @example
 * ```html
 * <app-calendar #calendar [date]="today" [eventHandler]="handleEventRequest"></app-calendar>
 * ```
 */
@Component({
  selector: 'app-calendar[date]',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarComponent implements OnInit, ICalendarComponent {

  //INPUT
  /**
   * The date to show the calendar at.
   */
  @Input() public date!: Date;
  /**
   * The calendar type to show, see {@link CalendarType}
   */
  @Input() public calendarType?: CalendarType = Number(localStorage.getItem("calendar_type_view") ?? 0);
  @Input() public eventHandler: CalendarEventHandler;
  @Input() set settings(value: CalendarSettings) {
    this.weekSettings = value;
    this.monthSettings = value;
    this.yearSettings = value;
  }

  @Input() set dayTemplate(value: TemplateRef<DayTemplateContext>) {
    this.weekDayTemplate = value;
    this.monthDayTemplate = value;
    this.yearDayTemplate = value;
  };

  //Settings and templates per calendar type.
  @Input() public weekSettings: CalendarSettings;
  @Input() public monthSettings: CalendarSettings;
  @Input() public yearSettings: CalendarSettings;

  @Input() public weekDayTemplate: TemplateRef<DayTemplateContext>;
  @Input() public monthDayTemplate: TemplateRef<DayTemplateContext>;
  @Input() public yearDayTemplate: TemplateRef<DayTemplateContext>;

  @Output() public eventClick: EventEmitter<CalendarClickEvent> = new EventEmitter();

  protected CalendarType = CalendarType

  protected get firstDayOfWeek(): Date {
    const firstDayOfWeek = new Date(this.date);

    firstDayOfWeek.setDate(this.date.getDate() - ((this.date.getDay() || 7) - 1));

    return firstDayOfWeek;
  }

  protected get lastDayOfWeek(): Date {
    const lastDayOfWeek = new Date(this.date);

    lastDayOfWeek.setDate(this.date.getDate() + (7 - this.date.getDay() || 7))

    return lastDayOfWeek;
  }

  constructor() { }

  ngOnInit(): void {
  }

  public refreshEvents(): void {
    //TODO
  }

  /**
   * Sets the calendar's date to today.
   */
  public stepToToday(): void {
    this.date = new Date();
  }

  /**
   * Changes the calendar type.
   * @param type The calendar type.
   */
  public setCalendarType(type: CalendarType) {
    this.calendarType = type;
    localStorage.setItem("calendar_type_view", type.toString());
  }

  protected stepToNextDate(): void {
    //Step to next date.
    switch (this.calendarType) {
      case CalendarType.WEEK:
        this.date.setDate(this.date.getDate() + 7);
        break;
      case CalendarType.MONTH:
        this.date.setMonth(this.date.getMonth() + 1);
        break;
      case CalendarType.YEAR:
        this.date.setFullYear(this.date.getFullYear() + 1);
        break;
    }

    //Trigger change detection
    this.date = new Date(this.date);
  }

  protected stepToPreviousDate(): void {
    //Step to previous date.
    switch (this.calendarType) {
      case CalendarType.WEEK:
        this.date.setDate(this.date.getDate() - 7);
        break;
      case CalendarType.MONTH:
        this.date.setMonth(this.date.getMonth() - 1);
        break;
      case CalendarType.YEAR:
        this.date.setFullYear(this.date.getFullYear() - 1);
        break;
    }

    //Trigger change detection
    this.date = new Date(this.date);
  }

}